import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faFileContract,
  faMinus,
  faPlus,
  faReceipt,
  faTicket,
  faTriangleExclamation,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Promociones.module.css";
import { Modal } from "../../../components/ui";
import { useAuth } from "../../../context/authContext";
import useRechargeFlow from "../../../hooks/useRechargeFlow";
import { generateIdempotencyKey, raffleService } from "../../../services/api";
import {
  MAX_TICKETS_PER_PURCHASE,
  buyErrorInfo,
  formatPromoDateTimeInline,
  promoMoney,
  safeExternalUrl,
  ticketOutcome,
  toAmount,
} from "./promoUtils";

/** Respuestas tras las cuales no se sabe si el cobro se hizo: se reintenta con la misma clave. */
const isUncertainFailure = (res) => res.code === "network" || res.status >= 500;

/** Por qué no se puede comprar (cuando `can_buy` es false o no queda cupo). */
function unavailableReason(raffle, perUserLeft) {
  const display = String(raffle?.display_status || "").toLowerCase();
  if (display === "upcoming") {
    const opens = formatPromoDateTimeInline(raffle.starts_at);
    return opens ? `Las ventas abren el ${opens}.` : "Las ventas aún no han comenzado.";
  }
  if (display === "inactive") return "Las ventas de esta rifa ya cerraron.";
  if (raffle?.tickets_left != null && Number(raffle.tickets_left) <= 0) {
    return "Las boletas de esta rifa se agotaron.";
  }
  if (perUserLeft <= 0) {
    return `Ya tienes el máximo de boletas permitido por usuario (${raffle.max_tickets_per_user}).`;
  }
  return "La compra de boletas no está disponible en este momento.";
}

/**
 * Caja de compra de boletas con el saldo del usuario.
 * Cada intento confirmado usa un `Idempotency-Key` nuevo; si la respuesta no llega
 * (error de red / 5xx) el reintento reutiliza la misma clave para no cobrar dos veces.
 *
 * @param {object} props
 * @param {object} props.raffle      Detalle de GET /raffles/{slug}/
 * @param {() => void} props.onRefresh   Recarga el detalle (boletas restantes, mis boletas).
 * @param {(code: string) => void} props.onOpenReceipt
 */
export default function RaffleBuyBox({ raffle, onRefresh, onOpenReceipt }) {
  const navigate = useNavigate();
  const { isAuthenticated, user, profile, loadProfile, updateBalance } = useAuth();
  const { openRecharge, rechargeModals } = useRechargeFlow();

  const [quantity, setQuantity] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [modalError, setModalError] = useState(null); // error incierto: se reintenta en el modal
  const [error, setError] = useState(null); // { message, action }
  const [receipt, setReceipt] = useState(null);
  const attemptRef = useRef(null); // { key, quantity }

  const price = toAmount(raffle.ticket_price);
  const myActiveTickets = useMemo(
    () =>
      (Array.isArray(raffle.my_tickets) ? raffle.my_tickets : []).filter(
        (t) => ticketOutcome(t, raffle).key !== "refunded"
      ).length,
    [raffle]
  );
  const maxPerUser = Number(raffle.max_tickets_per_user) || 0;
  const perUserLeft = maxPerUser > 0 ? Math.max(0, maxPerUser - myActiveTickets) : Infinity;
  const ticketsLeft = raffle.tickets_left == null ? Infinity : Math.max(0, Number(raffle.tickets_left));
  const maxQuantity = Math.min(MAX_TICKETS_PER_PURCHASE, perUserLeft, ticketsLeft);
  const purchasable = Boolean(raffle.can_buy) && maxQuantity >= 1;
  const total = price * quantity;

  const rawBalance = user?.balance?.amount ?? profile?.balance?.amount;
  const balanceKnown = rawBalance !== undefined && rawBalance !== null && rawBalance !== "";
  const balance = toAmount(rawBalance);
  const insufficientKnown = balanceKnown && balance < total;

  // Mantener la cantidad dentro de los límites cuando cambian (p. ej. tras comprar).
  useEffect(() => {
    if (!Number.isFinite(maxQuantity) || maxQuantity < 1) return;
    setQuantity((q) => Math.min(Math.max(1, q), maxQuantity));
  }, [maxQuantity]);

  const changeQuantity = (next) => {
    const n = Math.floor(Number(next));
    if (!Number.isFinite(n)) return;
    const upper = Number.isFinite(maxQuantity) ? maxQuantity : MAX_TICKETS_PER_PURCHASE;
    setQuantity(Math.min(Math.max(1, n), Math.max(1, upper)));
    setError(null);
  };

  const goToLogin = () => {
    navigate("/auth", {
      state: {
        mode: "login",
        returnTo: `/promociones/${raffle.slug}`,
        message: "Inicia sesión para comprar tus boletas",
      },
    });
  };

  const handleBuyClick = () => {
    setError(null);
    if (!isAuthenticated) {
      goToLogin();
      return;
    }
    setModalError(null);
    setConfirmOpen(true);
    // Saldo fresco para el resumen del diálogo.
    loadProfile?.();
  };

  const closeConfirm = () => {
    if (buying) return;
    setConfirmOpen(false);
    if (modalError) {
      // Un intento quedó sin confirmar: refrescar por si sí se registró.
      onRefresh?.();
      setModalError(null);
    }
  };

  const handleConfirm = async () => {
    if (buying) return;
    if (!attemptRef.current || attemptRef.current.quantity !== quantity) {
      attemptRef.current = { key: generateIdempotencyKey(), quantity };
    }
    setBuying(true);
    setModalError(null);
    const res = await raffleService.buy(raffle.slug, quantity, attemptRef.current.key);
    setBuying(false);

    if (res.ok) {
      attemptRef.current = null;
      setConfirmOpen(false);
      setReceipt(res.data);
      setQuantity(1);
      if (res.data?.new_balance != null) updateBalance(toAmount(res.data.new_balance));
      loadProfile?.();
      onRefresh?.();
      return;
    }

    const info = buyErrorInfo(res, raffle);
    if (isUncertainFailure(res)) {
      // Se conserva la clave: el reintento devuelve el mismo comprobante si ya se cobró.
      setModalError(info.message);
      return;
    }

    attemptRef.current = null;
    setConfirmOpen(false);
    setError(info);
    if (info.action === "reload") onRefresh?.();
    if (res.code === "insufficient_balance") loadProfile?.();
  };

  const errorAction = () => {
    if (!error?.action) return null;
    if (error.action === "recharge") {
      return (
        <button type="button" className={styles.inlineBtn} onClick={openRecharge}>
          <FontAwesomeIcon icon={faWallet} /> Recargar
        </button>
      );
    }
    if (error.action === "play") {
      return (
        <button type="button" className={styles.inlineBtn} onClick={() => navigate("/apuestas")}>
          <FontAwesomeIcon icon={faTicket} /> Comprar billete
        </button>
      );
    }
    if (error.action === "login") {
      return (
        <button type="button" className={styles.inlineBtn} onClick={goToLogin}>
          Iniciar sesión
        </button>
      );
    }
    return null;
  };

  if (receipt) {
    const tickets = Array.isArray(receipt.tickets) ? receipt.tickets : [];
    const termsUrl = safeExternalUrl(receipt.terms_url);
    return (
      <div className={styles.buyBox}>
        <div className={styles.successHead} role="status">
          <FontAwesomeIcon icon={faCircleCheck} className={styles.successIcon} />
          <div>
            <p className={styles.successTitle}>¡Compra exitosa!</p>
            <p className={styles.successText}>
              {receipt.replayed
                ? "Esta compra ya estaba registrada; no se te cobró de nuevo."
                : `Pagaste ${promoMoney(receipt.total)} con tu saldo.`}
            </p>
          </div>
        </div>

        <dl className={styles.summaryList}>
          <div>
            <dt>Comprobante</dt>
            <dd className={styles.mono}>{receipt.receipt_code}</dd>
          </div>
          <div>
            <dt>Boletas</dt>
            <dd>{receipt.quantity ?? tickets.length}</dd>
          </div>
        </dl>

        {tickets.length > 0 && (
          <div>
            <p className={styles.blockLabel}>Tus números</p>
            <ul className={styles.ticketChips}>
              {tickets.map((n) => (
                <li key={n} className={styles.ticketChip}>
                  {n}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.buyActions}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => onOpenReceipt?.(receipt.receipt_code)}
          >
            <FontAwesomeIcon icon={faReceipt} /> Ver comprobante
          </button>
          {termsUrl && (
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.secondaryBtn}
            >
              <FontAwesomeIcon icon={faFileContract} /> Términos y condiciones
            </a>
          )}
        </div>

        <div className={styles.buyLinks}>
          {raffle.can_buy && (
            <button type="button" className={styles.textBtn} onClick={() => setReceipt(null)}>
              Comprar más boletas
            </button>
          )}
          <button
            type="button"
            className={styles.textBtn}
            onClick={() => navigate("/perfil", { state: { section: "rifas" } })}
          >
            Ver mis rifas
          </button>
        </div>
        {rechargeModals}
      </div>
    );
  }

  if (!purchasable) {
    return (
      <div className={styles.buyBox}>
        <p className={styles.unavailable}>{unavailableReason(raffle, perUserLeft)}</p>
        {error && (
          <div className={styles.buyError} role="alert">
            <p>{error.message}</p>
          </div>
        )}
      </div>
    );
  }

  const upper = Number.isFinite(maxQuantity) ? maxQuantity : MAX_TICKETS_PER_PURCHASE;

  return (
    <div className={styles.buyBox}>
      <label className={styles.blockLabel} htmlFor="raffle-qty">
        ¿Cuántas boletas quieres?
      </label>
      <div className={styles.stepper}>
        <button
          type="button"
          className={styles.stepperBtn}
          onClick={() => changeQuantity(quantity - 1)}
          disabled={quantity <= 1}
          aria-label="Quitar una boleta"
        >
          <FontAwesomeIcon icon={faMinus} />
        </button>
        <input
          id="raffle-qty"
          type="number"
          inputMode="numeric"
          min={1}
          max={upper}
          value={quantity}
          onChange={(e) => changeQuantity(e.target.value)}
          className={styles.stepperInput}
        />
        <button
          type="button"
          className={styles.stepperBtn}
          onClick={() => changeQuantity(quantity + 1)}
          disabled={quantity >= upper}
          aria-label="Agregar una boleta"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
      <p className={styles.hint}>
        Máximo {upper} {upper === 1 ? "boleta" : "boletas"} en esta compra
        {maxPerUser > 0 ? ` · ${maxPerUser} por usuario` : ""}.
      </p>

      <div className={styles.totalRow}>
        <span>
          {quantity} × {promoMoney(price)}
        </span>
        <strong>{promoMoney(total)}</strong>
      </div>

      <button type="button" className={styles.buyBtn} onClick={handleBuyClick}>
        <FontAwesomeIcon icon={faWallet} />
        Comprar con mi saldo
      </button>
      {!isAuthenticated && (
        <p className={styles.hint}>Debes iniciar sesión para comprar.</p>
      )}

      {error && (
        <div className={styles.buyError} role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <div>
            <p>{error.message}</p>
            {errorAction()}
          </div>
        </div>
      )}

      <Modal isOpen={confirmOpen} onClose={closeConfirm} title="Confirma tu compra" size="sm">
        <div className={styles.confirm}>
          <p className={styles.confirmLead}>
            Vas a comprar {quantity} {quantity === 1 ? "boleta" : "boletas"} de{" "}
            <strong>{raffle.title}</strong>.
          </p>
          <dl className={styles.summaryList}>
            <div>
              <dt>Valor por boleta</dt>
              <dd>{promoMoney(price)}</dd>
            </div>
            <div>
              <dt>Se descontará de tu saldo</dt>
              <dd className={styles.confirmTotal}>{promoMoney(total)}</dd>
            </div>
            {balanceKnown && (
              <>
                <div>
                  <dt>Saldo disponible</dt>
                  <dd>{promoMoney(balance)}</dd>
                </div>
                {!insufficientKnown && (
                  <div>
                    <dt>Saldo después de la compra</dt>
                    <dd>{promoMoney(balance - total)}</dd>
                  </div>
                )}
              </>
            )}
          </dl>

          {insufficientKnown && (
            <div className={styles.buyError} role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <div>
                <p>No tienes saldo suficiente para esta compra.</p>
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={() => {
                    setConfirmOpen(false);
                    openRecharge();
                  }}
                >
                  <FontAwesomeIcon icon={faWallet} /> Recargar
                </button>
              </div>
            </div>
          )}

          {modalError && (
            <div className={styles.buyError} role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <p>{modalError}</p>
            </div>
          )}

          <p className={styles.hint}>
            Al confirmar aceptas los términos y condiciones de la rifa.
          </p>

          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={closeConfirm}
              disabled={buying}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleConfirm}
              disabled={buying || insufficientKnown}
              aria-busy={buying}
            >
              {buying
                ? "Procesando…"
                : modalError
                  ? "Reintentar compra"
                  : `Pagar ${promoMoney(total)}`}
            </button>
          </div>
        </div>
      </Modal>
      {rechargeModals}
    </div>
  );
}
